import type {
  ClaimCredentialsResponse,
  PollPairSessionResponse,
  StartPairSessionResponse,
} from "@medicoach/types";

import {
  clearCachedJwt,
  getCachedJwt,
  getPairedDevice,
  isJwtFresh,
  setCachedJwt,
  unpair,
} from "./auth";

/**
 * Tablet-side API client. All authenticated calls flow through `kioskFetch`
 * which:
 *   1. Lazily mints a fresh kiosk JWT (via /kiosk/auth/session) if the
 *      cached one is missing or near expiry.
 *   2. Attaches the JWT as Authorization: Bearer <jwt>.
 *   3. On 401 → wipes cached JWT and retries once with a fresh mint.
 *   4. On 403 KIOSK_REVOKED → wipes ALL pairing state and surfaces a
 *      `KioskRevokedError` so the UI can redirect to /pair.
 */

const API_BASE =
  process.env.NEXT_PUBLIC_API_URL ?? "https://api.medicoach.co.za";

export class KioskNotPairedError extends Error {
  constructor() {
    super("Kiosk not paired");
  }
}
export class KioskRevokedError extends Error {
  constructor() {
    super("Kiosk has been revoked by an administrator");
  }
}
export class KioskApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly kioskCode?: string,
    public readonly details?: unknown
  ) {
    super(message);
  }
}

export interface KioskApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: { kioskCode?: string } & Record<string, unknown>;
  };
}

const fetchSessionToken = async (): Promise<string> => {
  const device = getPairedDevice();
  if (!device) throw new KioskNotPairedError();
  const res = await fetch(`${API_BASE}/kiosk/auth/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      kioskId: device.kioskId,
      institutionId: device.institutionId,
      apiKey: device.apiKey,
    }),
  });
  if (res.status === 403) {
    unpair();
    throw new KioskRevokedError();
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as KioskApiErrorBody;
    throw new KioskApiError(
      res.status,
      body.error?.code ?? "UNKNOWN",
      body.error?.message ?? "Failed to mint session token",
      body.error?.details?.kioskCode
    );
  }
  const json = (await res.json()) as { token: string; expiresIn: number };
  setCachedJwt(json.token, json.expiresIn);
  return json.token;
};

const ensureToken = async (): Promise<string> => {
  const cached = getCachedJwt();
  if (cached && isJwtFresh(cached)) return cached.token;
  return fetchSessionToken();
};

export const kioskFetch = async <T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> => {
  let token = await ensureToken();
  let res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${token}`,
      "Content-Type":
        (init.headers as Record<string, string>)?.["Content-Type"] ??
        "application/json",
    },
  });

  // 401 → token may have been invalidated server-side; refresh once.
  if (res.status === 401) {
    clearCachedJwt();
    token = await fetchSessionToken();
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        Authorization: `Bearer ${token}`,
        "Content-Type":
          (init.headers as Record<string, string>)?.["Content-Type"] ??
          "application/json",
      },
    });
  }

  if (res.ok) {
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  const body = (await res.json().catch(() => ({}))) as KioskApiErrorBody;
  if (body.error?.details?.kioskCode === "REVOKED") {
    unpair();
    throw new KioskRevokedError();
  }
  throw new KioskApiError(
    res.status,
    body.error?.code ?? "UNKNOWN",
    body.error?.message ?? "Request failed",
    body.error?.details?.kioskCode,
    body.error?.details
  );
};

// ----- Public (unauthenticated) endpoints used by the device-flow pair UI -----
//
// RFC 8628-style pair-session protocol. The tablet:
//   1. POSTs /start anonymously → receives sessionId, deviceCode (secret),
//      userCode (human-visible), approveUrl (QR target), expiresIn, pollInterval.
//   2. Polls /poll {sessionId, deviceCode} every pollInterval seconds.
//   3. On `credentials_issued`, persists apiKey via setPairedDevice, THEN
//      POSTs /poll/claim {sessionId, deviceCode, claimToken} to consume the
//      session (one-shot delivery; replays of /poll afterward return `consumed`).
//
// deviceCode + claimToken are in-memory secrets — NEVER persisted to
// localStorage. Only apiKey (long-lived) is persisted, via setPairedDevice.

const parseErrorBody = async (res: Response): Promise<KioskApiErrorBody> =>
  (await res.json().catch(() => ({}))) as KioskApiErrorBody;

export const startPairSession = async (): Promise<StartPairSessionResponse> => {
  const res = await fetch(`${API_BASE}/kiosk/auth/pair-session/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const body = await parseErrorBody(res);
    throw new KioskApiError(
      res.status,
      body.error?.code ?? "UNKNOWN",
      body.error?.message ?? "Failed to start pairing session",
      body.error?.details?.kioskCode,
      body.error?.details
    );
  }
  return (await res.json()) as StartPairSessionResponse;
};

export const pollPairSession = async (
  sessionId: string,
  deviceCode: string
): Promise<PollPairSessionResponse> => {
  const res = await fetch(`${API_BASE}/kiosk/auth/pair-session/poll`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sessionId, deviceCode }),
  });
  if (!res.ok) {
    const body = await parseErrorBody(res);
    throw new KioskApiError(
      res.status,
      body.error?.code ?? "UNKNOWN",
      body.error?.message ?? "Failed to poll pair session",
      body.error?.details?.kioskCode,
      body.error?.details
    );
  }
  return (await res.json()) as PollPairSessionResponse;
};

export const claimCredentials = async (
  sessionId: string,
  deviceCode: string,
  claimToken: string
): Promise<ClaimCredentialsResponse> => {
  const res = await fetch(`${API_BASE}/kiosk/auth/pair-session/claim`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sessionId, deviceCode, claimToken }),
  });
  if (!res.ok) {
    const body = await parseErrorBody(res);
    throw new KioskApiError(
      res.status,
      body.error?.code ?? "UNKNOWN",
      body.error?.message ?? "Failed to claim credentials",
      body.error?.details?.kioskCode,
      body.error?.details
    );
  }
  return (await res.json()) as ClaimCredentialsResponse;
};

// ----- Authenticated kiosk endpoints (typed wrappers) -----

export interface ResolveResult {
  player: {
    id: string;
    name: string;
    profilePictureUrl: string;
    shirtNumber?: number;
    position?: string;
  };
  userID: string | null;
  wallet: {
    balance: number;
    lifetimeEarned: number;
    lifetimeRedeemed: number;
  } | null;
  collectableOrders: Array<{
    id: string;
    orderNumber: string;
    created: number;
    items: Array<{
      productID: string;
      productName: string;
      productSlug: string;
      quantity: number;
      price: number;
      imageKey: string | null;
      variantID: string | null;
      variantName: string | null;
      fulfilled: boolean;
    }>;
    status: string;
    total: number;
  }>;
  identitySource: "rotating_qr" | "badge_code";
}

export const resolvePlayer = (input: {
  qrPayload?: string;
  badgeCode?: string;
}): Promise<ResolveResult> =>
  kioskFetch<ResolveResult>("/kiosk/players/resolve", {
    method: "POST",
    body: JSON.stringify(input),
  });

export interface ScanLookupResult {
  product: { id: string; name: string; type: string; category: string };
  variant: { id: string; name: string; sku: string; price: number } | null;
  stock: {
    available: number;
    reserved: number;
    total: number;
    lowStockThreshold: number;
  };
}

export const scanLookup = (barcode: string): Promise<ScanLookupResult> =>
  kioskFetch<ScanLookupResult>("/kiosk/scan/lookup", {
    method: "POST",
    body: JSON.stringify({ barcode }),
  });

export interface ScannedItem {
  productID: string;
  variantID: string | null;
  sku: string;
  qty: number;
  orderID: string;
  scannedAt: number;
}

export interface KioskSession {
  id: string;
  state: string;
  playerID?: string;
  orderIDs?: string[];
  scannedItems: ScannedItem[];
  startedAt?: number;
  expiresAt?: number;
}

export const startSession = (input: {
  playerID: string;
  userID: string;
  orderIDs: string[];
}): Promise<{ session: KioskSession }> =>
  kioskFetch<{ session: KioskSession }>("/kiosk/sessions", {
    method: "POST",
    body: JSON.stringify(input),
  });

export const getSession = (sessionId: string): Promise<{ session: KioskSession }> =>
  kioskFetch<{ session: KioskSession }>(`/kiosk/sessions/${sessionId}`, {
    method: "GET",
  });

export interface ActiveSessionLookup {
  active: {
    id: string;
    playerID: string;
    startedAt: number;
  } | null;
}

/**
 * Orphan-recovery lookup. After a 409 SESSION_ACTIVE_KIOSK from startSession
 * the idle page calls this with `expecting=true` so the server retries the
 * eventually-consistent query a couple of times. Response carries playerID
 * ONLY — never the player's name/avatar/orders — so a mismatch can be
 * detected without leaking the previous staffer's player to the next one.
 */
export const getActiveSession = (
  options: { expecting?: boolean } = {}
): Promise<ActiveSessionLookup> =>
  kioskFetch<ActiveSessionLookup>(
    `/kiosk/sessions/active${options.expecting ? "?expecting=1" : ""}`,
    { method: "GET" }
  );

export const scanItem = (
  sessionId: string,
  input: {
    productID: string;
    variantID: string | null;
    sku: string;
    qty?: number;
  }
): Promise<{
  session: KioskSession;
  resolution: {
    orderID: string;
    lineQty: number;
    alreadyScanned: number;
    remaining: number;
  };
}> =>
  kioskFetch(`/kiosk/sessions/${sessionId}/scan-item`, {
    method: "POST",
    body: JSON.stringify(input),
  });

export const cancelSession = (
  sessionId: string,
  reason: string,
  staffPin?: string
): Promise<{ ok: true }> =>
  kioskFetch(`/kiosk/sessions/${sessionId}/cancel`, {
    method: "POST",
    body: JSON.stringify(staffPin ? { reason, staffPin } : { reason }),
  });

export interface CompletionResult {
  sessionId: string;
  completionId: string;
  fulfilledOrderIds: string[];
  fullyCompletedOrderIds: string[];
  itemsScanned: number;
}

export const completeSession = (
  sessionId: string,
  completionId: string
): Promise<CompletionResult> =>
  kioskFetch<CompletionResult>(`/kiosk/sessions/${sessionId}/complete`, {
    method: "POST",
    body: JSON.stringify({ completionId }),
  });

export const heartbeat = (): Promise<{
  revoked: boolean;
  configVersion: number;
  serverTime: number;
}> =>
  kioskFetch("/kiosk/auth/heartbeat", {
    method: "POST",
  });
